<template>
  <v-row>
    <v-col cols="12">
      <v-card class="ob-card" flat>
        <!-- HEADER BAND -->
        <div class="ob-header pl-base-bg">
          <div class="ob-header-icon">
            <v-icon color="#fff" size="22">mdi-cash-register</v-icon>
          </div>
          <div>
            <div class="ob-title">Opening Balances</div>
            <div class="ob-subtitle">Starting cash/bank/liability figures as of a date</div>
          </div>
        </div>

        <div class="pa-6 ob-scroll-area">
          <v-alert type="info" outlined dense border="left" class="ob-disclaimer">
            Enter what each account actually held on the day you started using
            ProfitLedgex's ledger. Any difference between what you enter here
            and your named liabilities is automatically posted to Owner's
            Capital. Saving replaces any opening balances entered before —
            it's a single point-in-time snapshot, not a running log.
          </v-alert>

          <v-row dense align="center" class="mb-4">
            <v-col cols="12" md="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formattedAsOf"
                    label="As of"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>

                <v-date-picker
                  v-model="asOf"
                  @input="dateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>

          <div v-if="loading" class="ob-empty-state">Loading...</div>

          <template v-else>
            <div class="ob-section-title">Assets</div>
            <v-simple-table class="ob-table mb-6">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account</th>
                  <th class="text-right">Opening Balance (Dr)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in assetRows" :key="row.accountId">
                  <td>{{ row.code }}</td>
                  <td>{{ row.name }}</td>
                  <td class="text-right">
                    <v-text-field
                      v-model="amounts[row.accountId]"
                      type="number"
                      outlined
                      dense
                      hide-details
                      class="ob-amount-field"
                    />
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <div class="ob-section-title">Liabilities</div>
            <v-simple-table class="ob-table mb-6">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Account</th>
                  <th class="text-right">Opening Balance (Cr)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in liabilityRows" :key="row.accountId">
                  <td>{{ row.code }}</td>
                  <td>{{ row.name }}</td>
                  <td class="text-right">
                    <v-text-field
                      v-model="amounts[row.accountId]"
                      type="number"
                      outlined
                      dense
                      hide-details
                      class="ob-amount-field"
                    />
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>
        </div>

        <div v-if="!loading" class="ob-sticky-footer">
          <div class="ob-doc-stat">
            <div class="ob-doc-label">Total Assets</div>
            <div class="ob-doc-value">{{ formatINR(totalAssets) }}</div>
          </div>
          <div class="ob-doc-stat">
            <div class="ob-doc-label">Total Liabilities</div>
            <div class="ob-doc-value">{{ formatINR(totalLiabilities) }}</div>
          </div>
          <div class="ob-doc-stat">
            <div class="ob-doc-label">Owner's Capital (plug)</div>
            <div class="ob-doc-value">{{ formatINR(totalAssets - totalLiabilities) }}</div>
          </div>

          <v-btn class="ob-save-btn" :loading="saving" @click="save">
            Save Opening Balances
          </v-btn>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "OpeningBalancesPage",

  data() {
    return {
      loading: false,
      saving: false,
      dateMenu: false,
      asOf: new Date().toISOString().slice(0, 10),
      amounts: {},
      rows: [],
    };
  },

  computed: {
    formattedAsOf() {
      if (!this.asOf) return "";
      const d = new Date(this.asOf);
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    },

    assetRows() {
      return this.rows.filter((r) => r.type === "ASSET");
    },

    liabilityRows() {
      return this.rows.filter((r) => r.type === "LIABILITY");
    },

    totalAssets() {
      return this.assetRows.reduce((sum, r) => sum + (Number(this.amounts[r.accountId]) || 0), 0);
    },

    totalLiabilities() {
      return this.liabilityRows.reduce((sum, r) => sum + (Number(this.amounts[r.accountId]) || 0), 0);
    },
  },

  created() {
    this.load();
  },

  methods: {
    formatINR(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value);
    },

    async load() {
      this.loading = true;
      try {
        const data = await this.$store.dispatch("openingBalances/getOpeningBalances");
        this.rows = data.accounts;

        const amounts = {};
        for (const row of data.accounts) {
          amounts[row.accountId] = row.openingBalance || 0;
        }
        this.amounts = amounts;

        if (data.asOf) {
          this.asOf = new Date(data.asOf).toISOString().slice(0, 10);
        }
      } catch (err) {
        console.error(err);
        this.$toast?.error(
          err?.response?.data?.message || "Failed to load opening balances",
        );
      } finally {
        this.loading = false;
      }
    },

    async save() {
      this.saving = true;
      try {
        const balances = this.rows
          .map((row) => ({
            accountId: row.accountId,
            amount: Number(this.amounts[row.accountId]) || 0,
          }))
          .filter((b) => b.amount !== 0);

        if (balances.length === 0) {
          this.$toast?.error("Enter at least one non-zero opening balance");
          return;
        }

        await this.$store.dispatch("openingBalances/setOpeningBalances", {
          asOf: this.asOf,
          balances,
        });

        this.$toast?.success("Opening balances saved successfully");
        await this.load();
      } catch (err) {
        console.error(err);
        this.$toast?.error(
          err?.response?.data?.message || "Failed to save opening balances",
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.ob-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
  display: flex;
  flex-direction: column;
}

/* The account list is the only part that scrolls — totals and Save
   stay pinned below it so they're never lost while filling in the
   19-row Asset/Liability list. */
.ob-scroll-area {
  max-height: 60vh;
  overflow-y: auto;
}

.ob-sticky-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #eef1f5;
  background: #f8fafc;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

.ob-sticky-footer .ob-doc-stat {
  flex: 1 1 160px;
}

.ob-sticky-footer .ob-save-btn {
  margin-left: auto;
}

.ob-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.ob-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ob-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.ob-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.ob-disclaimer {
  margin-bottom: 20px;
  font-size: 13px;
}

.ob-section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 10px;
}

.ob-table {
  border: 1px solid #eef1f5;
  border-radius: 0;
}

.ob-amount-field {
  max-width: 160px;
  margin-left: auto;
  margin-top: 8px;
  margin-bottom: 8px;
}

.ob-doc-stat {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 12px 14px;
}

.ob-doc-label {
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ob-doc-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-top: 4px;
}

.ob-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.ob-empty-state {
  text-align: center;
  padding: 48px 0;
  color: #94a3b8;
  font-size: 14px;
}

.v-menu__content {
  z-index: 9999 !important;
}
</style>
